import { Dispatch, FC, MouseEvent, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { Relative } from '@/components/DateRangeFilter';
import { Title } from '@/components/Dropdown';
import { Input } from '@/components/Forms';
import Tag from '@/components/Tag';
import Text from '@/components/Text';
import useDebounce from '@/hooks/useDebounce';
import useOnClickOutside from '@/hooks/useOnClickOutside';
import { Tags } from '@/pages/AssignTopics/components/AttachmentsField';

import {
  DropdownBox,
  DropdownButton,
  StyledText,
} from './InvoicesFilterDropdown';

const escapeRegExp = (value: string) =>
  value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const StyledTitle = styled(Title)`
  margin-bottom: 12px;
  padding: 0;
`;

const StyledTags = styled(Tags)`
  margin-bottom: 0;
  margin-top: 12px;
`;

const StyledInput = styled(Input)`
  height: 42px;
`;

const NotFoundPlaceholder = styled.div`
  padding: 9px 12px;
  background-color: ${({ theme }) => theme.colors.white};
  transition: 0.2s ease-out all;
  line-height: 22px;
  width: 100%;
  text-align: left;
  border-radius: 4px;
  outline: none;
`;

interface Props {
  options?: {
    name: string;
    value: number;
  }[];
  title: string;
  filter: number[];
  setFilter: Dispatch<React.SetStateAction<number[]>>;
}
const IdFilterDropdown: FC<Props> = ({ options, title, filter, setFilter }) => {
  const [showDropdown, setShowDropdown] = useState(false);

  const [creatorName, setCreatorName] = useState('');

  const [suggestions, setSuggestions] = useState<
    {
      name: string;
      value: number;
    }[]
  >(options ?? []);

  const debouncedName = useDebounce(creatorName, 400);

  const [values, setValues] = useState<number[]>(filter);

  const ref = useRef<HTMLInputElement>(null);
  useOnClickOutside(ref, () => setShowDropdown(false));

  const filteredSuggestions = suggestions?.filter(
    (suggestion) => !values.includes(suggestion.value),
  );

  useEffect(() => {
    setFilter(values);
  }, [values, setFilter]);

  useEffect(() => {
    if (debouncedName.length > 0) {
      setSuggestions(
        () =>
          options?.filter((item) =>
            item.name.toLowerCase().includes(debouncedName.toLowerCase()),
          ) ?? [],
      );
    } else {
      setSuggestions(options ?? []);
    }
  }, [debouncedName, options]);

  const highlightMatches = (str: string) =>
    debouncedName
      ? str
          .split(new RegExp(`(${escapeRegExp(debouncedName)})`, 'gi'))
          .map((part, index) =>
            index % 2 ? <span key={index}>{part}</span> : part,
          )
      : str;

  return (
    <div>
      <Relative>
        <StyledTitle $color="black" $type="smallBold">
          {title}
          <StyledText
            as="button"
            $color="gray"
            $type="extraSmall"
            disabled={!values.length}
            onClick={() => {
              setValues([]);
              setCreatorName('');
            }}
          >
            Clear
          </StyledText>
        </StyledTitle>
        <StyledInput
          placeholder="Select"
          ref={ref}
          // disabled={!filteredSuggestions?.length}
          onClick={() => setShowDropdown((prev) => !prev)}
          onChange={(e) => {
            setCreatorName(e.target.value);
          }}
          value={creatorName}
        />
        {showDropdown && (
          <DropdownBox>
            {!!filteredSuggestions.length ? (
              <>
                {filteredSuggestions.map((suggestion, index) => (
                  <DropdownButton
                    key={index}
                    type="button"
                    as="button"
                    onClick={(e: MouseEvent<HTMLButtonElement>) => {
                      e.stopPropagation();
                      setValues([...values, suggestion.value]);
                      setShowDropdown(false);
                    }}
                  >
                    <Text $color="gray" $type="captionSemibold">
                      {highlightMatches(suggestion.name)}
                    </Text>
                  </DropdownButton>
                ))}
              </>
            ) : (
              <NotFoundPlaceholder>
                <Text $color="gray" $type="captionSemibold">
                  No Data Found
                </Text>
              </NotFoundPlaceholder>
            )}
          </DropdownBox>
        )}
      </Relative>
      {!!values.length && (
        <StyledTags>
          <>
            {values.map((value) => {
              return (
                <Tag
                  key={value}
                  onDelete={(e: MouseEvent<HTMLButtonElement>) => {
                    e.stopPropagation();
                    setValues((prev) =>
                      prev.filter((prevValue) => prevValue !== value),
                    );
                  }}
                >
                  {options?.find((option) => option.value === value)?.name}
                </Tag>
              );
            })}
          </>
        </StyledTags>
      )}
    </div>
  );
};

export default IdFilterDropdown;
